import {
  Box,
  Checkbox,
  type CheckboxGroupProps,
  Divider,
  Flex,
  Loader,
  ScrollArea,
  Text,
} from "@mantine/core";
import { type ReactNode } from "react";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import { SectionDivider } from "src/styled";

import { useTheme } from "../../../../hooks";
import CheckboxGroupItem from "./CheckboxGroupItem";
import useCheckboxGroupData from "./useCheckboxGroupData";
import SearchInput from "../../LocalSearch/SearchInput";
import type { ComboboxItem } from "@mantine/core";

interface Props<T extends FieldValues, K extends ComboboxItem> {
  title?: string;
  data: K[];
  renderLabel?: (item: K) => ReactNode;
  name: Path<T>;
  control: Control<T>;
  checkboxGroupProps?: Omit<CheckboxGroupProps, "children">;
  scrollHeight?: number | string;
  error?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  grouped?: boolean;
  searchable?: boolean;
  full?: boolean;
  loading?: boolean;
  noScroll?: boolean;
  scrollAreaStyle?: React.CSSProperties;
  searchContainerStyle?: React.CSSProperties;
}

function CheckboxGroup<T extends FieldValues, K extends ComboboxItem>({
  title,
  data,
  name,
  control,
  checkboxGroupProps,
  scrollHeight,
  error,
  children,
  grouped,
  searchable,
  full,
  loading,
  noScroll,
  scrollAreaStyle,
  searchContainerStyle,
  ...itemProps
}: Props<T, K>) {
  const theme = useTheme();
  const {
    field: { value = [], onChange },
  } = useController({
    name,
    control,
  });

  const { groupedData, ungroupedData, searchStr, setSearchStr } =
    useCheckboxGroupData({ data, grouped });

  const isGroupSelected = (groupItems: K[]) => {
    return groupItems.every(item => (value as string[]).includes(item.value));
  };

  const isGroupPartiallySelected = (groupItems: K[]) => {
    return (
      groupItems.some(item => (value as string[]).includes(item.value)) &&
      !isGroupSelected(groupItems)
    );
  };

  const handleGroupToggle = (groupItems: K[], isSelected: boolean) => {
    const selected = [...(value as string[])];

    groupItems.forEach(item => {
      if (isSelected && !selected.includes(item.value)) {
        selected.push(item.value);
      } else if (!isSelected && selected.includes(item.value)) {
        selected.splice(selected.indexOf(item.value), 1);
      }
    });

    onChange(selected);
  };

  const handleChange = (v: string[]) => {
    const selected = [...v];

    const findItemByValue = (
      items: K[],
      targetValue: string,
      visited = new Set<string>(),
    ): K | null => {
      for (const currentItem of items) {
        if (visited.has(currentItem.value)) continue;

        if (currentItem.value === targetValue) {
          return currentItem;
        }

        if (currentItem.parts?.length) {
          const nextVisited = new Set(visited);
          nextVisited.add(currentItem.value);

          const foundChild = findItemByValue(
            currentItem.parts as K[],
            targetValue,
            nextVisited,
          );
          if (foundChild) return foundChild;
        }
      }

      return null;
    };

    const collectPartValues = (
      item: K,
      visited = new Set<string>(),
    ): string[] => {
      if (visited.has(item.value) || !item.parts?.length) return [];

      const nextVisited = new Set(visited);
      nextVisited.add(item.value);

      const partValues: string[] = [];
      item.parts.forEach((part: any) => {
        partValues.push(part.value);
        partValues.push(...collectPartValues(part as K, nextVisited));
      });

      return partValues;
    };

    const toggleParts = (lbid: string, clear: boolean = false) => {
      const selectedItem = findItemByValue(data, lbid);
      if (!selectedItem) return;

      const partValues = collectPartValues(selectedItem);

      partValues.forEach(partValue => {
        if (!clear) {
          if (!selected.includes(partValue)) selected.push(partValue);
        } else {
          if (selected.includes(partValue))
            selected.splice(selected.indexOf(partValue), 1);
        }
      });
    };

    const positiveDiff = v.filter(x => !(value as string[]).includes(x));
    positiveDiff.forEach(lbid => toggleParts(lbid));
    const negativeDiff = (value as string[]).filter(x => !selected.includes(x));

    if (negativeDiff.length > 0) {
      negativeDiff.forEach(lbid => toggleParts(lbid, true));
    }
    onChange(selected);
  };

  return (
    <>
      {title && (
        <SectionDivider mb={15} error={error}>
          {title}
        </SectionDivider>
      )}
      {children}
      {searchable && (
        <SearchInput
          value={searchStr}
          onChange={setSearchStr}
          containerStyles={{
            flex: 1,
            alignSelf: "flex-start",
            width: full ? "100%" : undefined,
            ...(noScroll
              ? {
                  position: "sticky",
                  top: 0,
                  left: 0,
                  zIndex: 1,
                  paddingTop: 20,
                  paddingBottom: 10,
                  margin: "-6px 12px 0 0",
                  backgroundColor: theme.colors.inputBackgroud?.[0],
                }
              : {}),
            ...searchContainerStyle,
          }}
        />
      )}
      <ScrollArea
        maw="95%"
        h={noScroll ? "100%" : scrollHeight}
        style={scrollAreaStyle}>
        {loading ? (
          <Flex
            justify="center"
            mt={10}
            style={{ height: noScroll ? "100%" : scrollHeight }}>
            <Loader />
          </Flex>
        ) : grouped ? (
          Object.entries(groupedData).map(([key, group]) => (
            <div key={key} style={{ overflowX: "hidden" }}>
              <Divider
                my="xs"
                label={
                  <Box
                    style={{
                      textTransform: "capitalize",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    ml={5}>
                    <Checkbox
                      checked={isGroupSelected(group)}
                      indeterminate={isGroupPartiallySelected(group)}
                      onChange={event => {
                        event.stopPropagation();
                        handleGroupToggle(group, event.currentTarget.checked);
                      }}
                      size="sm"
                    />
                    <Text size="md" c="dimmed">
                      {key == "ungrouped" ? "Logbooks" : key}
                    </Text>
                  </Box>
                }
                labelPosition="left"
              />
              <Checkbox.Group
                value={value}
                onChange={handleChange}
                {...checkboxGroupProps}>
                {group.map(item => (
                  <CheckboxGroupItem
                    key={item.value}
                    {...itemProps}
                    item={item}
                    wrapperStyles={{
                      marginLeft: "20px",
                      borderLeft: "2px solid",
                      borderColor: theme.colors.main[2],
                    }}
                    labelStyles={{ padding: "0px 10px", margin: "0px 20px" }}
                  />
                ))}
              </Checkbox.Group>
            </div>
          ))
        ) : (
          <Checkbox.Group
            value={value}
            onChange={handleChange}
            {...checkboxGroupProps}>
            {ungroupedData.map(item => (
              <CheckboxGroupItem key={item.value} {...itemProps} item={item} />
            ))}
          </Checkbox.Group>
        )}
      </ScrollArea>
    </>
  );
}

export default CheckboxGroup;
